<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { SliderRange, SliderRoot, SliderThumb, SliderTrack } from 'reka-ui'

const props = defineProps<{ level: string, labelledBy: string }>()

const emit = defineEmits<{ change: [level: string] }>()

const LEVELS = [
  { value: 0, mark: 'Less', description: 'Less Support' },
  { value: 1, mark: '', description: 'Moderate Support' },
  { value: 2, mark: 'More', description: 'More Support' }
]
const MAX_LEVEL = LEVELS.length - 1

function toSupportedLevel(level: string): number {
  const parsedLevel = Number(level)
  return LEVELS.some(step => step.value === parsedLevel) ? parsedLevel : 0
}

const sliderValues = ref([toSupportedLevel(props.level)])
watch(() => props.level, (level) => {
  sliderValues.value = [toSupportedLevel(level)]
})

const selectedLevel = computed(() => sliderValues.value[0])
const selectedLevelDescription = computed(() => LEVELS[selectedLevel.value].description)

function stepPosition(level: number): string {
  return `${(level / MAX_LEVEL) * 100}%`
}

function emitLevelChange(committedValues: number[]) {
  const [committedLevel] = committedValues
  if (committedLevel === toSupportedLevel(props.level)) {
    return
  }
  emit('change', String(committedLevel))
}
</script>

<template>
  <SliderRoot
    v-model="sliderValues"
    :min="0"
    :max="MAX_LEVEL"
    :step="1"
    thumb-alignment="overflow"
    class="relative mb-8 ml-[7px] flex h-3.5 w-[calc(100%-7px)] cursor-pointer touch-none select-none items-center"
    @value-commit="emitLevelChange"
  >
    <SliderTrack class="relative h-1 grow bg-gray-700">
      <SliderRange class="absolute h-full bg-blue-400" />
    </SliderTrack>
    <span
      v-for="step in LEVELS"
      :key="step.value"
      aria-hidden="true"
      class="pointer-events-none absolute size-3.5 -translate-x-1/2 rounded-full bg-white ring-2"
      :class="step.value <= selectedLevel ? 'ring-blue-400' : 'ring-gray-700'"
      :style="{ left: stepPosition(step.value) }"
    >
      <span class="absolute left-1/2 top-5 -translate-x-1/2 whitespace-nowrap text-text-sm text-gray-900">{{ step.mark }}</span>
    </span>
    <SliderThumb
      :aria-labelledby="labelledBy"
      :aria-valuetext="selectedLevelDescription"
      class="block size-3.5 rounded-full bg-blue-400 ring-2 ring-blue-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600"
    />
  </SliderRoot>
</template>
